// Feature: features/nma-payer-response-pipeline.md, features/nma-claims-beta-banner-and-blurred-summaries.md, features/nma-claims-performance-and-component-extraction.md
import React, { useState, useEffect, useMemo } from 'react';
import {
  Flex,
  Text,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  HStack,
} from '@chakra-ui/react';
import { formatCurrency } from './shared';

const PAGE_SIZE_OPTIONS = [50, 100, 250];
const DEFAULT_PAGE_SIZE = 100;

const NMARecordsTrackerTab = React.memo(({
  recordsTracker = [],
  onOpenClaimDetail,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordsTracker, pageSize]);

  const totalMatching = recordsTracker.length;
  const totalPages = Math.max(1, Math.ceil(totalMatching / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalMatching);

  const paginatedRecords = useMemo(
    () => recordsTracker.slice(startIdx, endIdx),
    [recordsTracker, startIdx, endIdx]
  );

  if (totalMatching === 0) {
    return (
      <Text color="gray.500" py={6}>
        No claims with medical records requests match the current filter.
      </Text>
    );
  }

  return (
    <>
      {totalMatching > PAGE_SIZE_OPTIONS[0] && (
        <Flex justify="space-between" align="center" mb={3} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" wrap="wrap" gap={2}>
          <Text fontSize="xs" color="gray.600">
            Showing{' '}
            <b>
              {(startIdx + 1).toLocaleString()}&ndash;{endIdx.toLocaleString()}
            </b>{' '}
            of <b>{totalMatching.toLocaleString()}</b> medical record requests
          </Text>
          <HStack spacing={2}>
            <Select
              size="xs"
              bg="white"
              w="105px"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((sz) => (
                <option key={sz} value={sz}>{sz} / page</option>
              ))}
            </Select>
            <Button
              size="xs"
              variant="outline"
              bg="white"
              isDisabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
              {safePage} / {totalPages}
            </Text>
            <Button
              size="xs"
              variant="outline"
              bg="white"
              isDisabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}

      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead bg="gray.100">
            <Tr>
              <Th>Claim #</Th>
              <Th>Case # / Patient</Th>
              <Th>Payer</Th>
              <Th>Records Status</Th>
              <Th>Requested Items</Th>
              <Th>Letter Date &amp; Window</Th>
              <Th>Submission Destination &amp; Summary</Th>
              <Th isNumeric>Billed $ (beta)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedRecords.map((rt) => (
              <Tr key={`${rt.case_id}-${rt.claim_number}`} _hover={{ bg: 'gray.50' }}>
                <Td fontWeight="bold">
                  <Text
                    as="span"
                    color="blue.600"
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={() => onOpenClaimDetail(rt)}
                  >
                    {rt.claim_number}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="xs" fontWeight="bold">{rt.nma_case_no}</Text>
                  <Text fontSize="xs" color="gray.600">{rt.patient_name || 'N/A'}</Text>
                </Td>
                <Td fontSize="xs" fontWeight="semibold">{rt.payer_name}</Td>
                <Td>
                  <Badge colorScheme={rt.records_sent ? 'green' : 'red'} variant="solid">
                    {rt.records_sent ? 'RECORDS SENT' : 'PENDING SUBMISSION'}
                  </Badge>
                  {rt.records_sent_dates?.length > 0 && (
                    <Text fontSize="10px" color="gray.500" mt={1}>
                      Sent: {rt.records_sent_dates[0]?.slice(0, 10)}
                    </Text>
                  )}
                </Td>
                <Td maxW="280px">
                  <Flex gap={1} wrap="wrap">
                    {(rt.records_requested_items || []).length > 0 ? (
                      rt.records_requested_items.map((item, i) => (
                        <Badge key={i} colorScheme="purple" variant="subtle" fontSize="10px">
                          {item}
                        </Badge>
                      ))
                    ) : (
                      <Badge colorScheme="purple" variant="outline" fontSize="10px">
                        Per EOB Remark (CO-252 / M127)
                      </Badge>
                    )}
                  </Flex>
                </Td>
                <Td fontSize="xs">
                  <Text>Letter: {rt.letter_date || 'EOB Notice'}</Text>
                  {rt.response_timeframe_days && (
                    <Badge colorScheme="orange" fontSize="10px">
                      {rt.response_timeframe_days} Days Window
                    </Badge>
                  )}
                </Td>
                <Td maxW="340px">
                  {rt.submission_destination && (
                    <Text fontSize="xs" fontWeight="semibold" color="blue.700" noOfLines={1}>
                      To: {rt.submission_destination}
                    </Text>
                  )}
                  <Text fontSize="xs" color="gray.600" noOfLines={2} title={rt.letter_summary}>
                    {rt.letter_summary || 'Medical records requested via EOB remark code.'}
                  </Text>
                </Td>
                <Td isNumeric fontWeight="bold">{formatCurrency(rt.total_billed)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
});

export default NMARecordsTrackerTab;
